import { createContext, createElement, ReactNode, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useUser } from "../context/UserContext";

interface Notification {
  message: string;
}

interface DraftOrderStatusChanged {
  draftOrderId: string | number;
  approvalStatus: string;
  [key: string]: unknown;
}

interface ServerMessage {
  type: "connected" | "pong" | "draft_order_status_changed" | "notification";
  [key: string]: unknown;
}

const MAX_RETRIES = 3;
const RECONNECT_DELAY = 1000;

const useNotificationSocketState = () => {
  const { user } = useUser();

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [draftOrderUpdates, setDraftOrderUpdates] = useState<DraftOrderStatusChanged[]>([]);

  const wsRef = useRef<WebSocket | null>(null);

  // Extract stable userId from context or fallback to token payload/sessionStorage
  const rawUserId = user?.userId ?? sessionStorage.getItem("userId");
  const userId = Array.isArray(rawUserId) ? rawUserId[0] : rawUserId;

  const clearDraftOrderUpdates = useCallback(() => {
    setDraftOrderUpdates([]);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("jsonwebtoken");
    console.log("===== WEBSOCKET USE EFFECT =====");
    console.log("Token exists:", Boolean(token));
    console.log("User ID:", user?.userId);

    if (!token || !userId) {
      console.log("WebSocket connection skipped: missing token or user ID");
      return;
    }

    const apiUrl = process.env.REACT_APP_BACKEND_URL ?? "http://127.0.0.1:5000";
    const websocketUrl = `${apiUrl.replace(/^http/, "ws")}/ws?token=${encodeURIComponent(token)}`;

    let retryCount = 0;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let disposed = false;

    const connect = () => {
      if (disposed) return;

      console.log("Connecting to WebSocket...");
      const websocket = new WebSocket(websocketUrl);
      wsRef.current = websocket;

      websocket.onopen = () => {
        if (disposed) {
          websocket.close();
          return;
        }
        console.log("WEBSOCKET CONNECTED");
        retryCount = 0;
        websocket.send(JSON.stringify({ type: "ping" }));
      };

      websocket.onmessage = (event: MessageEvent) => {
        if (disposed) return;

        try {
          const data = JSON.parse(event.data) as ServerMessage;

          switch (data.type) {
            case "connected":
              console.log("Backend WebSocket connection confirmed");
              break;

            case "pong":
              console.log("Backend WebSocket ping confirmed");
              break;

            case "draft_order_status_changed":
              console.log("APPROVAL NOTIFICATION RECEIVED:", data);
              setDraftOrderUpdates((prev) => [...prev, data as unknown as DraftOrderStatusChanged]);
              break;

            case "notification":
              setNotifications((prev) => [...prev, data as unknown as Notification]);
              break;

            default:
              console.warn("Unhandled WebSocket message type:", data);
          }
        } catch {
          console.error("Invalid WebSocket message:", event.data);
        }
      };

      websocket.onerror = () => {
        if (disposed) return;
        console.error("WebSocket connection error");
      };

      websocket.onclose = (event) => {
        if (wsRef.current === websocket) {
          wsRef.current = null;
        }
        console.log("WEBSOCKET DISCONNECTED:", event.code, event.reason);

        if (disposed) return;

        if (retryCount >= MAX_RETRIES) {
          console.warn("Maximum WebSocket reconnect attempts reached.");
          return;
        }

        retryCount += 1;
        const delay = RECONNECT_DELAY * 2 ** (retryCount - 1);
        console.log(`Reconnecting in ${delay}ms (${retryCount}/${MAX_RETRIES})`);

        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      disposed = true;

      if (reconnectTimer !== null) {
        clearTimeout(reconnectTimer);
      }

      const websocket = wsRef.current;
      if (websocket) {
        websocket.onopen = null;
        websocket.onmessage = null;
        websocket.onerror = null;
        websocket.onclose = null;

        if (websocket.readyState === WebSocket.CONNECTING || websocket.readyState === WebSocket.OPEN) {
          websocket.close();
        }
        wsRef.current = null;
      }
      console.log("WebSocket effect cleanup completed");
    };
  }, [user?.userId, userId]);

  return {
    notifications,
    draftOrderUpdates,
    clearDraftOrderUpdates,
  };
};

const NotificationSocketContext = createContext<ReturnType<typeof useNotificationSocketState> | null>(null);

export const NotificationSocketProvider = ({ children }: { children: ReactNode }) => {
  const value = useNotificationSocketState();

  return createElement(NotificationSocketContext.Provider, { value }, children);
};

export const useNotificationSocket = () => {
  const context = useContext(NotificationSocketContext);

  if (!context) {
    throw new Error("useNotificationSocket must be used within NotificationSocketProvider");
  }

  return context;
};
